import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid,
} from "recharts";
import { FileText, CheckCircle2, XCircle, CalendarPlus, Hourglass } from "lucide-react";
import { getStats, getAnalytics } from "../../api";
import HealthPanel from "./HealthPanel";

const COLORS = ["#F97316", "#2B8FD9", "#FBBF24", "#34D399", "#A78BFA", "#F87171"];

function Metric({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`grid h-8 w-8 place-items-center rounded-lg ${tint}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 text-3xl font-black">{value}</p>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-bold">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({ total: 0, qualified: 0, rejected: 0, todayNew: 0, incomplete: 0 });
  const [trend, setTrend] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [dist, setDist] = useState({ location: [], occupancy: [], ads: [], kol: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), getAnalytics()])
      .then(([s, a]) => {
        setStats(s);
        setTrend(a.trend.map((t) => ({ date: t.date, 問卷數: t.count })));
        setFunnel(a.funnel || []);
        setDist(a.dist);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5">
      <HealthPanel />
      {loading ? (
        <div className="py-16 text-center text-muted-foreground">載入中…</div>
      ) : (
       <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Metric icon={FileText} label="總問卷數" value={stats.total} tint="bg-orange-100 text-brand" />
        <Metric icon={CheckCircle2} label="符合資格" value={stats.qualified} tint="bg-green-100 text-green-600" />
        <Metric icon={XCircle} label="不符合資格" value={stats.rejected} tint="bg-gray-100 text-gray-500" />
        <Metric icon={CalendarPlus} label="今日新增" value={stats.todayNew} tint="bg-blue-100 text-blue-600" />
        <Metric icon={Hourglass} label="開始未完成" value={stats.incomplete} tint="bg-amber-100 text-amber-600" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="每日趨勢（近 30 天）">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="問卷數" stroke="#F97316" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="轉換漏斗">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 20, right: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {funnel.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DistPie title="地點分布" data={dist.location} />
        <DistPie title="住房率分布" data={dist.occupancy} />
        <DistPie title="廣告投放分布" data={dist.ads} />
        <DistPie title="網紅合作分布" data={dist.kol} />
      </div>
       </>
      )}
    </div>
  );
}

function DistPie({ title, data }) {
  const hasData = data.some((d) => d.value > 0);
  return (
    <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-bold">{title}</h3>
      {hasData ? (
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={32}>
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="grid h-[180px] place-items-center text-sm text-muted-foreground">尚無資料</div>
      )}
      <div className="mt-2 space-y-1">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
              {d.name}
            </span>
            <span className="font-medium">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
